import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api-client';

export interface AssignedUser {
  id: string;
  name: string;
  email: string;
  assignedAt: string;
  assignedBy?: string;
}

export interface UseRoleAssignmentsResult {
  data: AssignedUser[];
  loading: boolean;
  refetch: () => Promise<void>;
  assign: (userIds: string[]) => Promise<void>;
  unassign: (roleId: string, userId: string) => Promise<void>;
}

export function useRoleAssignments(roleId: string): UseRoleAssignmentsResult {
  const [data, setData] = useState<AssignedUser[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const body = await api.get<{ users: AssignedUser[] }>(`/roles/${roleId}/users`);
      setData(body.users);
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const assign = useCallback(
    async (userIds: string[]) => {
      const body = await api.post<{ users: AssignedUser[] }>(`/roles/${roleId}/users`, { userIds });
      setData(body.users);
    },
    [roleId],
  );

  const unassign = useCallback(
    async (_roleIdArg: string, userId: string) => {
      await api.delete(`/roles/${roleId}/users/${userId}`);
      setData((prev) => prev.filter((u) => u.id !== userId));
    },
    [roleId],
  );

  return { data, loading, refetch, assign, unassign };
}
